import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { can } from '../../utils/permissions';
import { dayText } from '../hub/cells';

/*
 * How rare a taxon is (docs/FIELD_AI_VISION_SPEC.md section 19): three small
 * bars -- local, Israel, the world -- and a status badge, from GET /rarity
 * (routes/rarity_index.py). Each tier has its colour AND its shape, and
 * fills as many of the four segments as it is rare, so the gauge reads
 * without colour too. Each bar is a button: a tap names its sources and the
 * date each was read. `unknown` reads "too little data", never rare.
 *
 * RarityGauge reads an isolate's own taxon (?specimen_id=) for the dossier's
 * Taxonomy tab, for specimen:read only (the server says the same).
 * RarityGaugeView draws one taxon's scales, from whichever answer holds
 * them; the live AI panel's gauge (phase A3) is to use it with ?ai_id=.
 *
 * Rarity is shown beside an identification, never part of it: nothing here
 * feeds the AI's scores.
 */
const BARS = ['local', 'national', 'global'];
const TIERS = ['common', 'uncommon', 'rare', 'very_rare'];

/** The shape and colour of each tier; unknown is hollow and grey. */
const LOOK = {
  common: { glyph: '●', color: 'var(--c-success-4-fg)' },
  uncommon: { glyph: '▲', color: 'var(--c-info-2-fg)' },
  rare: { glyph: '◆', color: 'var(--c-warning-text-6-fg)' },
  very_rare: { glyph: '★', color: 'var(--c-danger-fg)' },
  unknown: { glyph: '○', color: 'var(--c-text-muted-2-fg)' },
};

/** The status badge's look by the IUCN class. */
const STATUS_LOOK = {
  threatened: { glyph: '▲', color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)' },
  extinct: { glyph: '✕', color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)' },
  near_threatened: { glyph: '◆', color: 'var(--c-warning-text-6-fg)', background: 'var(--c-warning-soft-6)' },
  least_concern: { glyph: '●', color: 'var(--c-success-4-fg)', background: 'var(--c-success-soft)' },
  data_deficient: { glyph: '?', color: 'var(--c-text-muted-2-fg)', background: 'var(--c-surface)' },
  unknown: { glyph: '○', color: 'var(--c-text-muted-2-fg)', background: 'var(--c-surface)' },
};

function Segments({ tier }) {
  const filled = TIERS.indexOf(tier) + 1;
  const { color } = LOOK[tier] || LOOK.unknown;
  return (
    <span aria-hidden="true" style={styles.segments}>
      {TIERS.map((_, i) => (
        <span key={i} style={{
          ...styles.segment,
          ...(i < filled ? { background: color, borderColor: color } : { borderStyle: filled ? 'solid' : 'dashed' }),
        }} />
      ))}
    </span>
  );
}

/** One source of a scale as a line: what it counted, and when. */
function sourceLine(scale, source, t) {
  const date = dayText(source.as_of);
  const name = t(`details.source.${scale}_${source.source}`);
  if (source.source === 'iucn') return t('details.iucn', { source: name, code: source.code, date });
  if (source.source === 'curated') return t(source.endemic ? 'details.endemicYes' : 'details.endemicNo', { source: name, date });
  return t('details.count', { source: name, count: source.count, date });
}

function Details({ id, scaleKey, scale, t }) {
  const lines = (scale.sources || []).map((s) => sourceLine(scaleKey, s, t));
  return (
    <div id={id} data-testid={`rarity-details-${scaleKey}`} style={styles.details}>
      {scaleKey === 'local' && (
        <p style={styles.line}>
          {scale.area === 'radius' ? t('details.area.radius', { km: scale.radius_km })
            : t(`details.area.${scale.area ? 'region' : 'none'}`)}
        </p>
      )}
      {scale.score !== null && scale.score !== undefined && (
        <p style={styles.line}>{t('details.score', { score: Math.round(scale.score), population: scale.population })}</p>
      )}
      {lines.length === 0 && <p style={styles.line}>{t('details.none')}</p>}
      {lines.map((line, i) => <p key={i} style={styles.line}>{line}</p>)}
    </div>
  );
}

/** One taxon's four scales. */
export function RarityGaugeView({ taxon, stale = false, computedAt = null, idPrefix = 'rarity' }) {
  const { t } = useTranslation('rarity');
  const [open, setOpen] = useState(null);
  if (!taxon?.scales) return null;
  const { scales } = taxon;
  const toggle = (key) => setOpen((now) => (now === key ? null : key));
  const status = scales.status || {};
  const statusLook = STATUS_LOOK[status.tier] || STATUS_LOOK.unknown;

  return (
    <div data-testid="rarity-gauge-view" style={styles.view}>
      <ul style={styles.bars}>
        {BARS.map((key) => {
          const scale = scales[key] || { tier: 'unknown', sources: [] };
          const tier = LOOK[scale.tier] ? scale.tier : 'unknown';
          const detailsId = `${idPrefix}-details-${key}`;
          return (
            <li key={key} style={styles.row}>
              <button type="button" style={styles.bar} data-testid={`rarity-bar-${key}`}
                aria-expanded={open === key} aria-controls={detailsId}
                title={t('details.toggle', { scale: t(`scale.${key}`) })} onClick={() => toggle(key)}>
                <span style={styles.scale}>{t(`scale.${key}`)}</span>
                <Segments tier={tier} />
                <span data-testid={`rarity-tier-${key}`} data-tier={tier}
                  style={{ ...styles.tier, color: LOOK[tier].color }}>
                  <span aria-hidden="true">{LOOK[tier].glyph}</span> {t(`tier.${tier}`)}
                </span>
              </button>
              {open === key && <Details id={detailsId} scaleKey={key} scale={scale} t={t} />}
            </li>
          );
        })}
        <li style={styles.row}>
          <button type="button" style={styles.bar} data-testid="rarity-bar-status"
            aria-expanded={open === 'status'} aria-controls={`${idPrefix}-details-status`}
            title={t('details.toggle', { scale: t('scale.status') })} onClick={() => toggle('status')}>
            <span style={styles.scale}>{t('scale.status')}</span>
            <span data-testid="rarity-status" data-tier={status.tier || 'unknown'}
              style={{ ...styles.badge, color: statusLook.color, background: statusLook.background,
                       borderColor: statusLook.color }}>
              <span aria-hidden="true">{statusLook.glyph}</span> {t(`status.${STATUS_LOOK[status.tier] ? status.tier : 'unknown'}`)}
              {status.iucn && <bdi dir="ltr"> ({status.iucn})</bdi>}
            </span>
            {status.endemic && (
              <span data-testid="rarity-endemic" style={{ ...styles.badge, ...styles.endemic }}>
                <span aria-hidden="true">✦</span> {t('endemic')}
              </span>
            )}
          </button>
          {open === 'status' && <Details id={`${idPrefix}-details-status`} scaleKey="status" scale={status} t={t} />}
        </li>
      </ul>
      {stale && <p role="note" data-testid="rarity-stale" style={styles.note}>{t('stale', { date: dayText(computedAt) })}</p>}
    </div>
  );
}

/** An isolate's own taxon, for the dossier: specimen:read only. */
export default function RarityGauge({ specimenId, user }) {
  const { t } = useTranslation('rarity');
  const allowed = Boolean(specimenId) && can(user, 'specimen:read');
  const [state, setState] = useState({ status: 'loading', data: null });

  const load = useCallback(() => {
    if (!allowed) return () => {};
    let live = true;
    setState({ status: 'loading', data: null });
    Promise.resolve(api.get('/rarity', { params: { specimen_id: specimenId } }))
      .then((res) => { if (live) setState({ status: 'ready', data: res?.data || null }); })
      .catch(() => { if (live) setState({ status: 'failed', data: null }); });
    return () => { live = false; };
  }, [allowed, specimenId]);

  useEffect(() => load(), [load]);

  if (!allowed) return null;
  const taxon = state.data?.taxon;
  return (
    <section data-testid="rarity-gauge" aria-label={t('title')} style={styles.section}>
      <h4 style={styles.heading}>{t('title')}</h4>
      <p style={styles.note}>{t('intro')}</p>
      {state.status === 'loading' && <p style={styles.note}>{t('loading')}</p>}
      {state.status === 'failed' && (
        <p role="alert" style={styles.note}>
          {t('failed')}{' '}
          <button type="button" style={styles.retry} onClick={load}>{t('retry')}</button>
        </p>
      )}
      {state.status === 'ready' && !taxon?.scales && <p style={styles.note}>{t('noTaxon')}</p>}
      {state.status === 'ready' && taxon?.scales && (
        <RarityGaugeView taxon={taxon} stale={Boolean(state.data.stale)} computedAt={state.data.computed_at}
          idPrefix={`rarity-${specimenId}`} />
      )}
    </section>
  );
}

const styles = {
  section: {
    display: 'flex', flexDirection: 'column', gap: 6, padding: 10, borderRadius: 8, marginTop: 10,
    border: '1px solid var(--c-border)', background: 'var(--c-surface)',
  },
  heading: { margin: 0, fontSize: 13 },
  view: { display: 'flex', flexDirection: 'column', gap: 4 },
  bars: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  row: { display: 'flex', flexDirection: 'column', gap: 2 },
  bar: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '4px 6px', borderRadius: 6,
    border: '1px solid transparent', background: 'none', color: 'inherit', font: 'inherit', fontSize: 13,
    cursor: 'pointer', textAlign: 'start', minHeight: 32,
  },
  scale: { minWidth: 64, fontWeight: 600 },
  segments: { display: 'inline-flex', gap: 2 },
  segment: { display: 'inline-block', width: 14, height: 8, borderRadius: 2, border: '1px solid var(--c-border-strong)' },
  tier: { fontSize: 12, fontWeight: 600 },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px', borderRadius: 10, fontSize: 12,
    fontWeight: 600, border: '1px solid',
  },
  endemic: { color: 'var(--c-purple-fg)', background: 'var(--c-purple-soft)', borderColor: 'var(--c-purple-fg)' },
  details: { paddingInlineStart: 12, borderInlineStart: '2px solid var(--c-border)', marginInlineStart: 6 },
  line: { margin: '2px 0', fontSize: 12, color: 'var(--c-text-muted-fg)' },
  note: { margin: 0, fontSize: 12, color: 'var(--c-text-muted-fg)' },
  retry: { fontSize: 12, padding: '2px 8px', borderRadius: 6, border: '1px solid var(--c-border)',
           background: 'var(--c-surface)', cursor: 'pointer' },
};
